import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { translateBackendErrorCode } from "@jti/fe-common";
import { CasPurposesApi, OmpApi } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { Button } from "@jti/ui/components/Button";
import { MessageBox, MessageBoxVariant } from "@jti/ui/components/MessageBox";
import { Recaptchabar } from "@jti/ui/components/Recaptchabar";
import { Toaster } from "@jti/ui/components/Toaster";
import Checkmark from "@jti/ui/icons/checkmark-round.svg";
import { useConfig } from "@jti/ui/providers/useConfig";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { useUser } from "../../hooks/useUser";
import { useUserProfile } from "../../hooks/useUserProfile";
import { FormElement } from "../form-element/FormElement";
import { removeFalsyValues, transformDataWithConsents } from "./helpers/helpers";
import "./ContactForm.scss";
const ContactForm = ({ items, sendButtonLabel, description, title, type, inputs, serverErrorMessage, successMessage, showSuccessButton = false, showSuccessPageView = false, successButtonLabel, successButtonLink, formDisclaimer, isPrefilled, isPrefilledDisabled, nameFieldName, surnameFieldName, emailFieldName, confirmEmailFieldName, phoneNumberFieldName, }) => {
    const { isLoggedIn } = useUser();
    const { userProfile } = useUserProfile(isLoggedIn === true);
    const config = useConfig();
    const { __ } = useTranslations();
    const { unregister, register, reset, trigger, getValues, formState: { errors, isValid }, setValue, watch, } = useForm();
    const [consent, setConsent] = useState([]);
    const [formKey, setFormKey] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showMessageBox, setShowMessageBox] = useState(false);
    const formConfig = { register, errors, trigger, getValues, unregister, watch, setValue };
    const onSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        const formValues = formConfig.getValues();
        if (!formValues || Object.keys(formValues).length === 0) {
            setIsLoading(false);
            return;
        }
        trigger(Object.keys(formValues));
        if (!isValid) {
            setIsLoading(false);
            return;
        }
        OmpApi.init()
            .sendMessage(transformDataWithConsents(removeFalsyValues(formValues), consent), type)
            .then((responseData) => {
            setIsLoading(false);
            dispatchTrackingEvent(TrackingEvents.TrackingEvent, { event: "contact_form_submit" });
            if (responseData?.errors && serverErrorMessage) {
                toast.error(serverErrorMessage);
                return;
            }
            if (showSuccessPageView) {
                window.scroll(0, 0);
                setShowMessageBox(true);
            }
            else if (successMessage) {
                toast.success(successMessage);
            }
            reset();
            setFormKey((prevKey) => prevKey + 1);
        })
            .catch((error) => {
            setIsLoading(false);
            console.error(error);
            const translatedError = translateBackendErrorCode(error?.error_code);
            const errorMessage = translatedError || serverErrorMessage;
            if (errorMessage) {
                toast.error(errorMessage);
            }
        });
    };
    useEffect(() => {
        const isFetchNeeded = items?.some((item) => item.displayConsent === null ||
            (item.displayConsent === "onlyLoggedIn" && isLoggedIn) ||
            (item.displayConsent === "onlyNotLoggedIn" && !isLoggedIn));
        if (isFetchNeeded) {
            CasPurposesApi.init()
                .getPurposesByStep("contact_form")
                .then((response) => {
                setConsent(response);
            });
        }
    }, [isLoggedIn, items]);
    useEffect(() => {
        if (userProfile && isPrefilled) {
            reset({
                [nameFieldName]: userProfile?.personalInfo?.firstname,
                [surnameFieldName]: userProfile?.personalInfo?.lastname,
                [emailFieldName]: userProfile?.personalInfo?.email,
                [confirmEmailFieldName]: userProfile?.personalInfo?.email,
                [phoneNumberFieldName]: userProfile?.personalInfo?.telephone,
            });
        }
    }, [userProfile, reset]);
    const isFieldDisabled = (fieldId) => {
        return (isLoggedIn === true &&
            isPrefilledDisabled &&
            [nameFieldName, surnameFieldName, emailFieldName, confirmEmailFieldName, phoneNumberFieldName].includes(fieldId));
    };
    return (_jsxs("div", { className: "aem-contact-form__container", children: [_jsx(Toaster, {}), _jsx("h2", { "data-testid": "contact-form-title", className: "aem-contact-form__title", children: title }), _jsx("p", { "data-testid": "contact-form-description", className: "aem-contact-form__description", children: description }), _jsx(MessageBox, { variant: MessageBoxVariant.info, isVisible: showMessageBox, title: successMessage, icon: Checkmark, children: showSuccessButton && (_jsx(Button, { href: successButtonLink, className: "aem-contact-form__success-button", size: "small", children: successButtonLabel })) }), !showMessageBox && (_jsxs("form", { onSubmit: onSubmit, children: [items?.map((item) => {
                        if (!item?.resourceName) {
                            return null;
                        }
                        return (_jsx("div", { className: "aem-input-wrapper", children: _jsx(FormElement, { item: item, formConfig: formConfig, disabled: isFieldDisabled(item.id) }) }, item.id));
                    }), _jsx("p", { className: "aem-contact-form__disclaimer aem-contact-form__footer-disclaimer", children: formDisclaimer }), _jsx("div", { className: "aem-input-wrapper", children: _jsx(Button, { "data-testid": "send-button", color: "primary", type: "submit", isPending: isLoading, theme: "light", children: sendButtonLabel || __("contact.button.send") }) }), inputs?.map(({ key, value }) => {
                        const name = key.replace(/\s/g, "");
                        return _createElement("input", { ...register(name), type: "hidden", key: key, name: name, value: value });
                    })] }, formKey)), config.reCaptchaEnabled && config.brandingInformationEnabled && _jsx(Recaptchabar, {})] }));
};
export default ContactForm;
//# sourceMappingURL=ContactForm.js.map